// ─── Mermaid → SVG for export ─────────────────────────────────────────────────
// Diagrams are stored as mermaid source inside figure tokens. Nothing rendered
// them at export time, which produced two separate visible bugs:
//
//   * DOCX/PDF printed a bare "[Figure 2.1]" text placeholder where the diagram
//     should be, on the theory that diagrams "require server-side render".
//     They do not — mermaid is already a dependency and the export runs in the
//     browser.
//   * The HTML export shipped a <pre class="mermaid"> block plus a <script> tag
//     pointing at a CDN, so a downloaded file opened offline (or behind a
//     blocked CDN) showed raw diagram source to the reader.
//
// Rendering once, here, fixes both: DOCX embeds the SVG as PNG, and the HTML
// export inlines finished SVG and needs no network at all.

// Printable content width for A4 at the thesis margins, in CSS pixels.
export const PRINT_WIDTH_PX = 540

let _seq = 0
let _initialised = false

async function getMermaid() {
  const { default: mermaid } = await import('mermaid')
  if (!_initialised) {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'neutral',
      // securityLevel 'loose' matches the in-app viewer; without it mermaid
      // strips the label markup the prompts generate.
      securityLevel: 'loose',
      // htmlLabels must be off for export. With the default on, mermaid puts
      // node labels in <foreignObject>, which browsers refuse to rasterise when
      // the SVG is drawn through an <img> onto a canvas — the DOCX image comes
      // out blank or the load silently fails. Plain <text> labels rasterise.
      htmlLabels: false,
      flowchart: { htmlLabels: false, useMaxWidth: false },
      sequence:  { useMaxWidth: false },
      er:        { useMaxWidth: false },
      gantt:     { useMaxWidth: false },
    })
    _initialised = true
  }
  return mermaid
}

/**
 * Render mermaid source to an SVG string.
 * Returns null when the diagram is invalid or mermaid is unavailable, so callers
 * can fall back to showing the source rather than losing the figure entirely.
 *
 * @param {string} code
 * @returns {Promise<string|null>}
 */
export async function renderMermaidSvg(code) {
  const src = (code || '').trim()
  if (!src) return null
  if (typeof window === 'undefined') return null

  const id = `export-mermaid-${++_seq}`
  try {
    const mermaid = await getMermaid()

    // Validate first. On a parse failure mermaid.render draws a "Syntax error in
    // text" graphic and appends it to document.body — so without this check a
    // malformed diagram is embedded into the thesis as a picture of an error
    // message, and stray nodes accumulate in the host page.
    const valid = await mermaid.parse(src, { suppressErrors: true })
    if (!valid) {
      console.warn('[mermaidRenderer] invalid diagram, falling back to source')
      return null
    }

    const { svg } = await mermaid.render(id, src)
    if (!svg || /Syntax error in text/i.test(svg)) return null
    return svg
  } catch (err) {
    console.error('[mermaidRenderer] render failed, falling back to source:', err?.message)
    return null
  } finally {
    // mermaid leaves its scratch element behind on some failure paths.
    if (typeof document !== 'undefined') {
      document.getElementById(id)?.remove()
      document.getElementById(`d${id}`)?.remove()
    }
  }
}

/**
 * Mermaid emits width/height as percentages or omits them, which makes the SVG
 * unusable as a fixed-size canvas source for the DOCX image path. Force
 * explicit pixel dimensions, preserving the viewBox aspect ratio.
 *
 * @param {string} svg
 * @param {number} width
 * @returns {{ svg: string, width: number, height: number }}
 */
export function sizeSvg(svg, width = 560) {
  const viewBox = /viewBox="([\d.\-\s]+)"/i.exec(svg || '')
  let height = Math.round(width * 0.75)

  if (viewBox) {
    const [, , vbW, vbH] = viewBox[1].trim().split(/\s+/).map(Number)
    if (vbW > 0 && vbH > 0) height = Math.round(width * (vbH / vbW))
  }

  // Cap so a tall flowchart cannot run off the page.
  const MAX_HEIGHT = 720
  if (height > MAX_HEIGHT) {
    width  = Math.round(width * (MAX_HEIGHT / height))
    height = MAX_HEIGHT
  }

  const sized = (svg || '')
    .replace(/\swidth="[^"]*"/i,  ` width="${width}"`)
    .replace(/\sheight="[^"]*"/i, ` height="${height}"`)
    .replace(/style="[^"]*max-width:[^"]*"/i, '')

  return { svg: sized, width, height }
}

/**
 * Replace every <pre class="mermaid"> block in an HTML string with finished SVG.
 *
 * Both export paths previously shipped the source block plus a CDN <script>, so
 * a downloaded file opened offline (or behind a blocked CDN) showed raw diagram
 * source, and the print-to-PDF path raced a 1s timer against that download.
 * Inlining here makes the output self-contained and deterministic.
 *
 * A diagram that fails to render keeps its source block: visible and fixable
 * beats an empty gap where a figure should be.
 *
 * @param {string} html
 * @returns {Promise<string>}
 */
export async function inlineExportMermaid(html) {
  if (!html) return html || ''
  const blocks = [...html.matchAll(/<pre[^>]*class="mermaid"[^>]*>([\s\S]*?)<\/pre>/gi)]
  if (!blocks.length) return html

  let out = html
  for (const [full, escaped] of blocks) {
    const code = escaped
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"').replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
    const svg = await renderMermaidSvg(code)
    // 540px, not 620: A4 with the thesis margins (1in top/bottom/right, 1.5in
    // left for binding) leaves 146.5mm ~= 554px of content width, so a 620px
    // diagram overran the right margin on every PDF.
    if (svg) out = out.replace(full, sizeSvg(svg, PRINT_WIDTH_PX).svg)
  }
  return out
}
